import { useState, useEffect, useCallback } from "react";
import { debounce } from "lodash";
import { Grid, Table, Placeholder } from "semantic-ui-react";
import { TbFileSearch } from "react-icons/tb";
import { MdModeEditOutline, MdDeleteOutline } from "react-icons/md";
import { CommonTable, TitleBar, InputText } from "../../../components";
import SupplierEditModal from "../supplierEdit/SupplierEditModal";
import SupplierDetailsModal from "../supplierDetails/SupplierDetailsModal";
import {
  Supplier,
  useDeleteSupplierById,
  useGetSuppliers,
} from "../../../api/supplier";
import PaginationView from "../../../components/pagination/Pagination";
import { SupplierListCol } from "../../../config/tableColConstant";
import { useGetCountriesData } from "../../../api/static-data";
import ConfirmModal from "../../../components/confirmViewModal/ConfirmModal";
import { errorView } from "../../../helpers/ErrorHandler";
import { useForm } from "react-hook-form";
import SupplierFilter from "./SupplierFilter";

const SupplierList = () => {
  const [supplierEdit, setSupplierEdit] = useState<Supplier | null>(null);
  const [supplierDetails, setSupplierDetails] = useState<Supplier | null>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchKey, setSearchKey] = useState("");
  const [filters, setFilters] = useState<{
    is_supplier_as_user?: boolean;
    supplier_type: number[];
    orderBy: string;
  }>({
    is_supplier_as_user: undefined,
    supplier_type: [],
    orderBy: "createdAtDesc",
  });

  const { register, watch } = useForm();
  const [searchSupplier] = watch(["searchSupplier"]);

  const [visibleDeleteModal, setVisibleDeleteModal] = useState<boolean>(false);
  const [deleteData, setDeleteData] = useState<Supplier | null>(null);

  const {
    data: countriesData,
    isLoading: isCountriesDataLoading,
  } = useGetCountriesData();

  const {
    data: supplierData,
    isLoading: isLoadingSupplier,
    refetch,
  } = useGetSuppliers({
    pageNumber,
    pageSize,
    searchKey,
    ...filters,
  });

  const suppliers: Supplier[] = supplierData?.result || [];
  const totalPages = Math.ceil(
    (supplierData?.total || 0) / (supplierData?.pageSize || 1)
  );

  const debouncedSearch = useCallback(
    debounce((value: string) => {
      setSearchKey(value);
    }, 500),
    []
  );

  useEffect(() => {
    debouncedSearch(searchSupplier || "");
    return () => {
      debouncedSearch.cancel();
    };
  }, [searchSupplier, debouncedSearch]);

  const refetchWithFilters = (
    isSupplierAsUser: boolean | undefined,
    supplierTypes: number[],
    orderBy: string
  ) => {
    setFilters({
      is_supplier_as_user: isSupplierAsUser,
      supplier_type: supplierTypes,
      orderBy,
    });
  };

  const enableSupplierEdit = () => {
    setSupplierEdit(supplierDetails);
    setSupplierDetails(null);
  };

  const { isLoading, mutate } = useDeleteSupplierById();

  const handleSupplierDeleteConfirmation = () => {
    if (deleteData?.ingredients?.length) {
      errorView("This Supplier has Ingredients");
      setVisibleDeleteModal(false);
      return;
    }
    if (deleteData?.is_supplier_as_user) {
      errorView("This Supplier registered as a user");
      setVisibleDeleteModal(false);
      return;
    }
    deleteSupplierDetails(deleteData);
  };

  const deleteSupplierDetails = (supplier: any) => {
    mutate(supplier._id, {
      onSuccess: () => {
        refetch();
      },
    });
    setVisibleDeleteModal(false);
    setDeleteData(null);
  };

  const loadTableData = () => {
    if (isLoadingSupplier) {
      return (
        <>
          {[...Array(5)].map((_, i) => (
            <Table.Row key={`placeholder_${i}`}>
              <Table.Cell colSpan={5}>
                <Placeholder>
                  <Placeholder.Line length="full" />
                  <Placeholder.Line length="very long" />
                </Placeholder>
              </Table.Cell>
            </Table.Row>
          ))}
        </>
      );
    }

    return suppliers.map((supplier: Supplier, index: number) => {
      const copyProducts = [...supplier.products];
      copyProducts.shift();
      return [
        <Table.Row className="tbleR" key={`supplier_${index}`}>
          <Table.Cell>
            <p>{supplier["supplier_name"]} </p>
          </Table.Cell>
          <Table.Cell>
            <p>{supplier?.["supplier_contact_name"] || "-"}</p>
          </Table.Cell>
          <Table.Cell>
            <p>{supplier?.["supplier_email"] || "-"}</p>
          </Table.Cell>
          <Table.Cell>
            <p>{supplier?.["supplier_phone"] || "-"}</p>
          </Table.Cell>
          <Table.Cell>
            <Grid centered>
              <Grid.Column computer={4} tablet={16} mobile={16}>
                <TbFileSearch
                  cursor={"pointer"}
                  onClick={() => {
                    setSupplierDetails(supplier);
                  }}
                  size={24}
                  color="var(--tableEditIcon)"
                />
              </Grid.Column>
              <Grid.Column computer={4} tablet={16} mobile={16}>
                <MdModeEditOutline
                  cursor={"pointer"}
                  onClick={() => setSupplierEdit(supplier)}
                  size={24}
                  color="var(--tableEditIcon)"
                />
              </Grid.Column>
              <Grid.Column computer={4} tablet={16} mobile={16}>
                <MdDeleteOutline
                  cursor="pointer"
                  size={24}
                  color="var(--tableEditIcon)"
                  onClick={() => {
                    setVisibleDeleteModal(true);
                    setDeleteData(supplier);
                  }}
                />
              </Grid.Column>
            </Grid>
          </Table.Cell>
        </Table.Row>,
      ];
    });
  };

  return (
    <>
      <TitleBar titleName="Supplier" />
      <Grid>
        <Grid.Column computer={4} tablet={8} mobile={16}>
          <InputText
            register={register}
            name="searchSupplier"
            placeholder="Search Supplier"
            defaultValues={searchKey}
            onChangeFunction={(e: any) => {
            }}
          />
        </Grid.Column>
        <Grid.Column computer={2} tablet={4} mobile={8}>
          <SupplierFilter refetchWithFilters={refetchWithFilters} />
        </Grid.Column>
      </Grid>
      <Grid>
        <Grid.Column computer={16} tablet={16} mobile={16}>
          <CommonTable
            enableTableFooter
            tableHeaderData={SupplierListCol}
            tableFooterChild={
              <PaginationView
                currentPageNo={pageNumber}
                totalPages={totalPages}
                pageSize={pageSize}
                currentSelectPage={(page: number) => {
                  setPageNumber(page);
                }}
                changePageSize={(pageSize: number) => {
                  setPageSize(pageSize);
                }}
              />
            }
          >
            {isCountriesDataLoading ? (
              <Table.Row>
                <Table.Cell colSpan={5} textAlign="center">
                  <Placeholder>
                    <Placeholder.Line length="full" />
                  </Placeholder>
                </Table.Cell>
              </Table.Row>
            ) : (
              loadTableData()
            )}
          </CommonTable>
        </Grid.Column>
      </Grid>
      <SupplierEditModal
        visibleModal={!!supplierEdit}
        supplier={supplierEdit}
        closeModal={() => setSupplierEdit(null)}
        onSuccessEdit={() => {
          setSupplierEdit(null);
          refetch();
        }}
      />
      <SupplierDetailsModal
        visibleModal={!!supplierDetails}
        supplier={supplierDetails}
        setVisibleModal={() => setSupplierDetails(null)}
        countriesData={countriesData}
        setVisibleEdit={enableSupplierEdit}
      />
      <ConfirmModal
        viewModal={visibleDeleteModal}
        closeModal={() => setVisibleDeleteModal(false)}
        cancel={() => setVisibleDeleteModal(false)}
        approve={() => handleSupplierDeleteConfirmation()}
        title="Delete Supplier Data"
        subTitle="Are you sure you want to delete your supplier data?"
      />
    </>
  );
};

export default SupplierList;